import { Injectable } from '@angular/core';
import { TransmittedRemoteActionDaoService } from '../transmitted-remote-action-dao.service';
import { KeyValuePersistenceService } from 'src/app/_services/persistence/key-value-persistence/key-value-persistence.service';
import { RemoteAction } from 'src/app/_model/remote-action/remote-action';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import {
  DbPersistenceService,
  Query,
} from 'src/app/_services/persistence/db-persistence/db-persistence.service';
import { AppConfig } from 'src/app/app.config';
import { TransmittedRemoteActionDbTableDefinition } from './transmitted-remote-action-db-table-definition';

// CONSTANTS
const PURGE_TIMESTAMP_KEY = 'last_remote_actions_purge';

/**
 * DataAccessObject for already transmitted remote actions
 */
@Injectable()
export class TransmittedRemoteActionDbDaoService
  implements TransmittedRemoteActionDaoService
{
  // Lifecycle
  constructor(
    private logger: LoggingService,
    private keyValueStorage: KeyValuePersistenceService,
    private dbPersistenceService: DbPersistenceService,
    private transmittedRemoteActionTableDefinition: TransmittedRemoteActionDbTableDefinition,
  ) {}

  // TransmittedRemoteActionDaoService
  public async addRemoteAction(remoteAction: RemoteAction): Promise<void> {
    try {
      let db = await this.dbPersistenceService.getImsDb();

      let remoteActionQuery: Query = {
        statement:
          'INSERT OR REPLACE INTO ' +
          this.transmittedRemoteActionTableDefinition.tableName +
          '(endpoint, data, cachable, fifo, authenticationRequired, ' +
          ' rawRequestTimestamp, editedRequestTimestamp, executionTimestamp, ' +
          ' shortLabel, shortLabelTranslate, longLabel, longLabelTranslate, hideInDayProtocol)' +
          ' VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?)',
        parameters: [
          remoteAction.endpoint,
          JSON.stringify(remoteAction.data),
          remoteAction.cachable,
          remoteAction.fifo,
          remoteAction.authenticationRequired,
          remoteAction.rawRequestTimestamp,
          remoteAction.editedRequestTimestamp,
          remoteAction.executionTimestamp,
          remoteAction.shortLabel,
          remoteAction.shortLabelTranslate,
          remoteAction.longLabel,
          remoteAction.longLabelTranslate,
          remoteAction.hideInDayProtocol ?? false,
        ],
      };
      await this.dbPersistenceService.executeStatement(db, remoteActionQuery);
    } catch (error) {
      this.logger.log(
        'Error saving remote action to db',
        error,
        '-',
        error?.message,
      );
    }
  }

  public async getRemoteActions(): Promise<RemoteAction[]> {
    try {
      let db = await this.dbPersistenceService.getImsDb();
      let remoteActions: RemoteAction[] = [];

      let today = new Date();
      today.setHours(0, 0, 0, 0);

      let data = await this.dbPersistenceService.executeQuery(db, {
        statement:
          'SELECT * FROM ' +
          this.transmittedRemoteActionTableDefinition.tableName +
          ' WHERE executionTimeStamp >= ?;',
        parameters: [today.getDate()],
      });
      for (let i = 0; i < data.values.length; i++) {
        let row = data.values[i];
        remoteActions.push(
          new RemoteAction({
            id: row['id'],
            endpoint: row['endpoint'],
            data: JSON.parse(row['data']),
            cachable: row['cachable'] === 1,
            fifo: row['fifo'] === 1,
            authenticationRequired: row['authenticationRequired'] === 1,
            rawRequestTimestamp: row['rawRequestTimestamp'],
            editedRequestTimestamp: row['editedRequestTimestamp'],
            executionTimestamp: row['executionTimestamp'],
            shortLabel: row['shortLabel'],
            shortLabelTranslate: row['shortLabelTranslate'] === 1,
            longLabel: row['longLabel'],
            longLabelTranslate: row['longLabelTranslate'] === 1,
            hideInDayProtocol: row['hideInDayProtocol'] === 1,
          }),
        );
      }

      return remoteActions;
    } catch (error) {
      this.logger.log('Error reading remote action from db', error);
    }
    return [];
  }

  public async purgeRemoteActions(): Promise<void> {
    let lastPurge: Date = undefined;
    try {
      let lastPurgeString =
        await this.keyValueStorage.load(PURGE_TIMESTAMP_KEY);
      lastPurge = new Date(lastPurgeString);
    } catch (error) {
      this.logger.log(
        this.constructor.name,
        '- Could not get last remote action purge timestamp',
        error?.message,
      );
    }

    let today = new Date();
    today.setHours(0, 0, 0, 0);

    if (lastPurge && lastPurge >= today) {
      return;
    }

    try {
      let db = await this.dbPersistenceService.getImsDb();

      let today = new Date();
      today.setHours(0, 0, 0, 0);

      await this.dbPersistenceService.executeStatement(db, {
        statement:
          'DELETE FROM ' +
          this.transmittedRemoteActionTableDefinition.tableName +
          ' WHERE executionTimeStamp < ?;',
        parameters: [today.getTime()],
      });

      await this.keyValueStorage.store(
        PURGE_TIMESTAMP_KEY,
        JSON.stringify(today.getTime()),
      );
    } catch (error) {
      this.logger.log('Error reading purging remoteactions', error);
    }
  }

  public async reset() {
    await this.clear();
  }

  public async clear() {
    let db = await this.dbPersistenceService.getImsDb();
    await this.dbPersistenceService.truncateTable(
      db,
      this.transmittedRemoteActionTableDefinition.tableName,
    );
    await this.keyValueStorage.delete(PURGE_TIMESTAMP_KEY);
  }
}
